import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiX, FiChevronLeft, FiChevronRight, FiDownload, FiFileText } from "react-icons/fi";
import {
  Backdrop, SheetNav, SheetBody, Hero, Group, GroupLabel, Row, IconButton, IconTile,
} from "../../activity-logs/styles";
import { ActionButton, CreditsSheet, Equation, TruncatedText } from "../styles";
import { MoneyValue } from "./parts";
import { formatCNPJ, formatCount, formatCurrency } from "../dashboardData";
import { documentTitle, downloadXml, nameForItem, useXmlInfo } from "../xmlDocs";
import { formatDate, formatRate, titleCase } from "../detailData";

const shortKey = (nfe) => (nfe && nfe.length > 14 ? `…${nfe.slice(-10)}` : nfe || "Sem documento");

function Field({ label, value }) {
  if (!value) return null;
  return (
    <Row $dense>
      <div className="main"><div className="subtitle" style={{ marginTop: 0 }}>{label}</div></div>
      <div className="trailing" style={{ flexShrink: 1, minWidth: 0 }}>
        <span className="value" style={{ fontWeight: 400, wordBreak: "break-all", textAlign: "right" }}>{value}</span>
      </div>
    </Row>
  );
}

function XmlAction({ chave }) {
  if (!chave) return null;
  return (
    <ActionButton type="button" onClick={() => downloadXml(chave)} style={{ margin: "14px auto 0" }}>
      <FiDownload /> Baixar XML
    </ActionButton>
  );
}

function ItemView({ item, metodologia, color }) {
  const { info, loading } = useXmlInfo(item.nfe);
  const name = item.descricao || nameForItem(info, item);
  const route = item.ufOrigem || item.ufDestino ? `${item.ufOrigem || "?"} → ${item.ufDestino || "?"}` : "";
  return (
    <>
      <Hero>
        <div style={{ fontSize: "1.9em", fontWeight: 600, letterSpacing: "-0.03em" }}>
          <MoneyValue value={item.creditoRecuperavel} />
        </div>
        <h3 style={{ fontSize: "1em", fontWeight: 500, marginTop: 6 }}>
          {name || (loading ? "Lendo o XML…" : item.ncm ? `Sem nome na API · NCM ${item.ncm}` : item.credito?.titulo || "Item sem descrição")}
        </h3>
        <div className="hero-sub">{formatDate(item.dataEmissao)}{item.status ? ` · ${titleCase(item.status)}` : ""}</div>
        <XmlAction chave={item.nfe} />
      </Hero>

      <Equation $compact $color={color}>
        <div><span className="l">Base</span><span className="v">{formatCurrency(item.valorProduto)}</span></div>
        <span className="op">×</span>
        <div><span className="l">Alíquota</span><span className="v">{formatRate(item.aliquotaAplicavel)}</span></div>
        <span className="op">=</span>
        <div className="result"><span className="l">Crédito</span><span className="v">{formatCurrency(item.creditoRecuperavel)}</span></div>
      </Equation>

      <GroupLabel style={{ marginTop: 22 }}><span>Documento</span></GroupLabel>
      <Group>
        <Field label="Chave" value={item.nfe} />
        <Field label="Emissão" value={formatDate(item.dataEmissao)} />
        <Field label="CFOP" value={item.cfop} />
        <Field label="NCM" value={item.ncm} />
        <Field label="Rota" value={route} />
        <Field label="Operação" value={titleCase(item.operacao)} />
      </Group>

      <GroupLabel style={{ marginTop: 22 }}><span>Fornecedor</span></GroupLabel>
      <Group>
        <Field label="Nome" value={item.fornecedor} />
        <Field label="CNPJ" value={formatCNPJ(item.cnpjFornecedor)} />
      </Group>

      {metodologia?.baseLegal && (
        <>
          <GroupLabel style={{ marginTop: 22 }}><span>Fundamento</span></GroupLabel>
          <Group>
            <Field label="Base legal" value={metodologia.baseLegal} />
            <Field label="Registro de origem" value={metodologia.registroOrigem} />
          </Group>
        </>
      )}

      {item.observacoes?.length > 0 && (
        <>
          <GroupLabel style={{ marginTop: 22 }}><span>Observações</span></GroupLabel>
          <Group>
            {item.observacoes.map((obs, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Row key={i} $dense>
                <div className="main" style={{ fontSize: "0.84em", lineHeight: 1.45 }}>
                  {typeof obs === "string" ? obs : obs.texto || JSON.stringify(obs)}
                </div>
              </Row>
            ))}
          </Group>
        </>
      )}
    </>
  );
}

function DocumentView({ document: doc, onOpen, color }) {
  const { info } = useXmlInfo(doc.nfe);
  const name = documentTitle(info);
  return (
    <>
      <Hero>
        <IconTile $size={56} $color={color}><FiFileText /></IconTile>
        <h3>{name || shortKey(doc.nfe)}</h3>
        <div className="hero-sub">{[name && shortKey(doc.nfe), formatDate(doc.date), `${doc.items.length} itens`].filter(Boolean).join(" · ")}</div>
        <div style={{ marginTop: 12, fontSize: "1.6em", fontWeight: 600, letterSpacing: "-0.03em" }}>
          <MoneyValue value={doc.total} />
        </div>
        <XmlAction chave={doc.nfe} />
      </Hero>

      <GroupLabel style={{ marginTop: 22 }}><span>Itens do documento</span><span>{doc.items.length}</span></GroupLabel>
      <Group>
        {doc.items.map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Row key={item.id || i} $clickable onClick={() => onOpen(item)}>
            <div className="main">
              {/* Vindo da tela do fornecedor, o documento mistura impostos: o tipo identifica cada item */}
              <div className="title"><TruncatedText>{item.descricao || nameForItem(info, item) || item.credito?.titulo || `Item ${i + 1}`}</TruncatedText></div>
              <div className="subtitle">{[item.credito?.tipo, item.cfop && `CFOP ${item.cfop}`, item.ncm && `NCM ${item.ncm}`].filter(Boolean).join(" · ")}</div>
            </div>
            <div className="trailing">
              <span className="value">{formatCurrency(item.creditoRecuperavel)}</span>
              <FiChevronRight className="chevron" />
            </div>
          </Row>
        ))}
      </Group>
    </>
  );
}

// Todas as ocorrências de um NCM (ou de um tipo de crédito, quando não há NCM)
const GROUP_STEP = 50;
function GroupView({ group, onOpen, color }) {
  const [visible, setVisible] = useState(GROUP_STEP);
  const n = group.items.length;
  return (
    <>
      <Hero>
        <IconTile $size={56} $color={color}><FiFileText /></IconTile>
        <h3>{group.name || group.label}</h3>
        <div className="hero-sub">
          {[group.name && group.label, `${formatCount(n)} ${n === 1 ? "ocorrência" : "ocorrências"}`, group.docs > 0 && `${formatCount(group.docs)} documentos`].filter(Boolean).join(" · ")}
        </div>
        <div style={{ marginTop: 12, fontSize: "1.6em", fontWeight: 600, letterSpacing: "-0.03em" }}>
          <MoneyValue value={group.total} />
        </div>
      </Hero>

      <GroupLabel style={{ marginTop: 22 }}><span>Ocorrências</span><span>{formatCount(n)}</span></GroupLabel>
      <Group>
        {group.items.slice(0, visible).map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Row key={item.id || i} $clickable onClick={() => onOpen(item)}>
            <div className="main">
              <div className="title"><TruncatedText>{[formatDate(item.dataEmissao), item.nfe ? shortKey(item.nfe) : "Sem chave"].join(" · ")}</TruncatedText></div>
              <div className="subtitle">{[item.credito?.tipo, item.cfop && `CFOP ${item.cfop}`, `base ${formatCurrency(item.valorProduto)}`, formatRate(item.aliquotaAplicavel)].filter(Boolean).join(" · ")}</div>
            </div>
            <div className="trailing">
              <span className="value">{formatCurrency(item.creditoRecuperavel)}</span>
              <FiChevronRight className="chevron" />
            </div>
          </Row>
        ))}
      </Group>
      {visible < n && (
        <ActionButton type="button" onClick={() => setVisible((v) => v + GROUP_STEP)} style={{ margin: "12px auto 0" }}>
          Mostrar mais {formatCount(Math.min(GROUP_STEP, n - visible))} de {formatCount(n - visible)}
        </ActionButton>
      )}
    </>
  );
}

// Abre um item direto ou um documento com seus itens, com navegação em pilha
export default function ItemSheet({ item, document: doc, group, metodologia, metodologias, color, onClose }) {
  const singleItem = item || (doc?.items.length === 1 ? doc.items[0] : null) || (group?.items.length === 1 ? group.items[0] : null);
  const [openedItem, setOpenedItem] = useState(null);
  const shown = openedItem || singleItem;
  const canGoBack = !!openedItem;
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <>
      <Backdrop onClick={onClose} />
      <CreditsSheet role="dialog" aria-label={shown ? "Detalhe do item" : "Detalhe do documento"}>
        <SheetNav $scrolled={scrolled}>
          {canGoBack
            ? <button type="button" className="back" onClick={() => setOpenedItem(null)}><FiChevronLeft /><span>{group ? "Voltar" : "Documento"}</span></button>
            : <span />}
          <div className="nav-title">{shown ? shown.descricao || "Item" : group ? group.name || group.label : shortKey(doc?.nfe)}</div>
          <IconButton className="close" onClick={onClose} title="Fechar" aria-label="Fechar"><FiX /></IconButton>
        </SheetNav>
        <SheetBody onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 40)}>
          <div className="page" key={shown?.id || doc?.key || group?.key}>
            {shown && <ItemView item={shown} metodologia={metodologias?.[shown.tipoCredito] || metodologia} color={color} />}
            {!shown && group && <GroupView group={group} onOpen={setOpenedItem} color={color} />}
            {!shown && !group && <DocumentView document={doc} onOpen={setOpenedItem} color={color} />}
          </div>
        </SheetBody>
      </CreditsSheet>
    </>,
    document.body
  );
}
